import { useCallback } from 'react'

import { Button, Checkbox, Divider, Group, Stack, Text, TextInput } from '@mantine/core'
import { hasLength, useForm } from '@mantine/form'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useTranslate } from 'react-admin'
import { PermissionApiDTO, RoleAttributesApiDTO } from '../../../codegen/generated/Api.schemas'
import { usePutApiRolesId } from '../../../codegen/generated/roles'

import { LXLoader } from '../../../components/LXLoader'
import { UsersHelper } from '../../../helpers/Users.helper'
import { RolePermissionCards } from './RolePermissionCards'

type UpdateRoleProps = {
  role?: RoleAttributesApiDTO
  onUpdate: () => void
}

export const UpdateRole = ({ role, onUpdate }: UpdateRoleProps) => {
  const t = useTranslate()
  const form = useForm({
    initialValues: {
      name: role?.name ?? '',
      permissions: role?.permissions ?? ([] as PermissionApiDTO[]),
    },
    validate: {
      name: hasLength({ min: 3 }),
      permissions: (value: PermissionApiDTO[]) => value.length === 0,
    },
  })

  const updateRoleMutation = usePutApiRolesId()

  const onUpdateRole = useCallback(async () => {
    try {
      if (role?.id) {
        const { name, permissions } = form.values
        await updateRoleMutation.mutateAsync({
          id: role.id,
          data: {
            name,
            permissions,
          },
        })
        onUpdate()
      }
    } catch (error) {
      console.error(error)
    }
  }, [form.values, updateRoleMutation.mutateAsync, role?.id, onUpdate])

  const onPermissionsChange = useCallback(
    (p: PermissionApiDTO[]) => {
      const permissions = UsersHelper.permissionsSync(p)
      form.setValues({
        permissions,
      })
    },
    [form.setValues],
  )

  if (!role) {
    return <LXLoader />
  }

  return (
    <form onSubmit={form.onSubmit(onUpdateRole)}>
      <Stack>
        <TextInput
          key={form.key('name')}
          mt="md"
          w="50%"
          size="md"
          label={<Text size="sm">{t('_generics.name')}</Text>}
          placeholder={t('placeholders.name')}
          {...form.getInputProps('name')}
        />
        <Divider my="md" />
        <Checkbox.Group
          value={form.values.permissions}
          defaultValue={role.permissions}
          onChange={values => onPermissionsChange(values as PermissionApiDTO[])}
          label={t('pages.users.permissions')}
        >
          <RolePermissionCards />
        </Checkbox.Group>
      </Stack>
      <Group justify="flex-start" mt="xl">
        <Button
          type="submit"
          size="compact-md"
          leftSection={<IconDeviceFloppy size={16} />}
          disabled={!form.isValid() || !form.isDirty()}
          loading={updateRoleMutation.isLoading}
        >
          {t('_generics.save')}
        </Button>
      </Group>
    </form>
  )
}
